// Vendored from apps/backoffice/src/components/ui/shadcn/toast.tsx
// Simplification vs backoffice source:
//   - Replaced Cross2Icon (@radix-ui/react-icons, not in onboarding deps)
//     with X from lucide-react (already a dep).
import { X } from 'lucide-react';
import React, { createContext, useCallback, useContext, useState } from 'react';

import { Button } from '@/components/ui/shadcn/button';
import { Card } from '@/components/ui/shadcn/card';
import { cn } from '@/lib/utils';

interface ToastData {
  id: string;
  title?: string;
  description?: string;
  variant?: 'success' | 'error' | 'warning' | 'info';
  actionLabel?: string;
  onAction?: () => void;
}

interface ToastContextValue {
  toasts: ToastData[];
  addToast: (toast: Omit<ToastData, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children, duration = 5000 }: { children: React.ReactNode; duration?: number }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (toast: Omit<ToastData, 'id'>) => {
      const id = Math.random().toString(36).slice(2, 11);
      setToasts((prev) => [...prev, { ...toast, id }]);
      setTimeout(() => removeToast(id), duration);
    },
    [duration, removeToast]
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <div aria-live="polite" aria-atomic="true" className="fixed right-4 top-4 z-[9999] flex flex-col gap-2">
        {toasts.map((toast) => (
          <Card
            key={toast.id}
            role={toast.variant === 'error' ? 'alert' : 'status'}
            className={cn(
              'min-w-[300px] animate-in slide-in-from-right border-l-4 p-4',
              toast.variant === 'success' && 'border-l-emerald-600',
              toast.variant === 'error' && 'border-l-red-600',
              toast.variant === 'warning' && 'border-l-amber-600',
              (!toast.variant || toast.variant === 'info') && 'border-l-blue-600'
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                {toast.title ? <p className="text-sm font-semibold text-foreground">{toast.title}</p> : null}
                {toast.description ? <p className="text-sm text-muted-foreground">{toast.description}</p> : null}
                {toast.actionLabel && toast.onAction ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      toast.onAction?.();
                      removeToast(toast.id);
                    }}
                  >
                    {toast.actionLabel}
                  </Button>
                ) : null}
              </div>
              <Button variant="ghost" size="icon-sm" onClick={() => removeToast(toast.id)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const Toast = Card;
export const ToastTitle = ({ children }: { children: React.ReactNode }) => <p className="text-sm font-semibold">{children}</p>;
export const ToastDescription = ({ children }: { children: React.ReactNode }) => <p className="text-sm text-muted-foreground">{children}</p>;
export const ToastAction = Button;
export const ToastClose = Button;
